import React, { useEffect, lazy, Suspense, useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PixelScripts from "@/components/PixelScripts";
import { SEOProvider } from "@/lib/seoOverrides";
// Lightweight slug arrays only — full data files (LANDING_PAGES, HUBS, SERVICES)
// are loaded inside the lazy page components that consume them, keeping the
// homepage initial bundle small.
import { LANDING_PAGE_SLUGS, HUB_SLUGS } from "@/lib/routeSlugs";

// Floating / sticky UI — below-the-fold or post-mount components.
// Lazy-loaded so they don't bloat the initial bundle on mobile.
// lazy() wrapper that lets index.js resolve the current route's chunk BEFORE the
// first render (the chunk is <link rel=preload>ed in the per-route HTML). When the
// module is already in hand the thenable resolves synchronously, so React never
// shows the Suspense fallback on initial load — the static hero shell swaps straight
// into the real page. Falls back to normal lazy behaviour on client-side navigation.
export const ROUTE_LOADERS = {};
function lazyRoute(name, loader) {
  let mod;
  ROUTE_LOADERS[name] = () => loader().then((m) => { mod = m; return m; });
  return lazy(() => (mod ? { then: (res) => res(mod) } : ROUTE_LOADERS[name]()));
}

const MobileStickyBookBar  = lazy(() => import(/* webpackChunkName: "c-MobileStickyBookBar" */ "@/components/MobileStickyBookBar"));
const MobileStickyQuoteBar = lazy(() => import(/* webpackChunkName: "c-MobileStickyQuoteBar" */ "@/components/MobileStickyQuoteBar"));
const FloatingContact      = lazy(() => import(/* webpackChunkName: "c-FloatingContact" */ "@/components/FloatingContact"));
const PreFooterCTA         = lazy(() => import(/* webpackChunkName: "c-PreFooterCTA" */ "@/components/PreFooterCTA"));

// ─────────────────────────────────────────────────────────────
// Code-split every non-home route. Webpack emits a separate JS chunk per page,
// fetched on demand. Cuts the initial JS bundle from ~1.27 MB to ~400 KB raw —
// dramatically reduces mobile parse/exec time (the dominant TBT contributor on
// low-end mobile CPUs). Each chunk is fetched in parallel during navigation;
// route transition feels identical because the chunk is small (~10-40 KB).
// ─────────────────────────────────────────────────────────────
const Home              = lazyRoute("p-Home", () => import(/* webpackChunkName: "p-Home" */ "@/pages/Home"));
const About             = lazyRoute("p-About", () => import(/* webpackChunkName: "p-About" */ "@/pages/About"));
const Services          = lazyRoute("p-Services", () => import(/* webpackChunkName: "p-Services" */ "@/pages/Services"));
const ServiceDetail     = lazyRoute("p-ServiceDetail", () => import(/* webpackChunkName: "p-ServiceDetail" */ "@/pages/ServiceDetail"));
const FCAMoneyPage      = lazyRoute("p-FCAMoneyPage", () => import(/* webpackChunkName: "p-FCAMoneyPage" */ "@/pages/FCAMoneyPage"));
const LandingPage       = lazyRoute("p-LandingPage", () => import(/* webpackChunkName: "p-LandingPage" */ "@/pages/LandingPage"));
const Resources         = lazyRoute("p-Resources", () => import(/* webpackChunkName: "p-Resources" */ "@/pages/Resources"));
const SampleFCAReport   = lazyRoute("p-SampleFCAReport", () => import(/* webpackChunkName: "p-SampleFCAReport" */ "@/pages/SampleFCAReport"));
const Article           = lazyRoute("p-Article", () => import(/* webpackChunkName: "p-Article" */ "@/pages/Article"));
const FAQ               = lazyRoute("p-FAQ", () => import(/* webpackChunkName: "p-FAQ" */ "@/pages/FAQ"));
const Contact           = lazyRoute("p-Contact", () => import(/* webpackChunkName: "p-Contact" */ "@/pages/Contact"));
const Privacy           = lazyRoute("p-Privacy", () => import(/* webpackChunkName: "p-Privacy" */ "@/pages/Privacy"));
const NotFound          = lazyRoute("p-NotFound", () => import(/* webpackChunkName: "p-NotFound" */ "@/pages/NotFound"));
const LocationsHub      = lazyRoute("p-LocationsHub", () => import(/* webpackChunkName: "p-LocationsHub" */ "@/pages/LocationsHub"));
const LocationPage      = lazyRoute("p-LocationPage", () => import(/* webpackChunkName: "p-LocationPage" */ "@/pages/LocationPage"));
const ChecklistLandingPage = lazyRoute("p-ChecklistLandingPage", () => import(/* webpackChunkName: "p-ChecklistLandingPage" */ "@/pages/ChecklistLandingPage"));
const WorkWithUs        = lazyRoute("p-WorkWithUs", () => import(/* webpackChunkName: "p-WorkWithUs" */ "@/pages/WorkWithUs"));
const ReferParticipant  = lazyRoute("p-ReferParticipant", () => import(/* webpackChunkName: "p-ReferParticipant" */ "@/pages/ReferParticipant"));
const ForSupportCoordinators = lazyRoute("p-ForSupportCoordinators", () => import(/* webpackChunkName: "p-ForSupportCoordinators" */ "@/pages/ForSupportCoordinators"));
const SupportCoordinatorsPack = lazyRoute("p-SupportCoordinatorsPack", () => import(/* webpackChunkName: "p-SupportCoordinatorsPack" */ "@/pages/SupportCoordinatorsPack"));
const OTFunctionalCapacityAssessment = lazyRoute("p-OTFunctionalCapacityAssessment", () => import(/* webpackChunkName: "p-OTFunctionalCapacityAssessment" */ "@/pages/OTFunctionalCapacityAssessment"));
const DSPFunctionalCapacityAssessment = lazyRoute("p-DSPFunctionalCapacityAssessment", () => import(/* webpackChunkName: "p-DSPFunctionalCapacityAssessment" */ "@/pages/DSPFunctionalCapacityAssessment"));
const FCADSPApplicationArticle = lazyRoute("p-FCADSPApplicationArticle", () => import(/* webpackChunkName: "p-FCADSPApplicationArticle" */ "@/pages/articles/FCADSPApplicationArticle"));
const Pricing           = lazyRoute("p-Pricing", () => import(/* webpackChunkName: "p-Pricing" */ "@/pages/Pricing"));
const AdsLandingPage    = lazyRoute("p-AdsLandingPage", () => import(/* webpackChunkName: "p-AdsLandingPage" */ "@/pages/AdsLandingPage"));
const AdsLayout         = lazy(() => import(/* webpackChunkName: "c-AdsLayout" */ "@/components/AdsLayout"));
const ThankYou          = lazyRoute("p-ThankYou", () => import(/* webpackChunkName: "p-ThankYou" */ "@/pages/ThankYou"));
const LearnHub          = lazyRoute("p-LearnHub", () => import(/* webpackChunkName: "p-LearnHub" */ "@/pages/LearnHub"));
const LearnArticle      = lazyRoute("p-LearnArticle", () => import(/* webpackChunkName: "p-LearnArticle" */ "@/pages/LearnArticle"));
const HubPage           = lazyRoute("p-HubPage", () => import(/* webpackChunkName: "p-HubPage" */ "@/pages/HubPage"));
const ConditionPage     = lazyRoute("p-ConditionPage", () => import(/* webpackChunkName: "p-ConditionPage" */ "@/pages/ConditionPage"));
const WhyTrustUs        = lazyRoute("p-WhyTrustUs", () => import(/* webpackChunkName: "p-WhyTrustUs" */ "@/pages/WhyTrustUs"));
const Methodology       = lazyRoute("p-Methodology", () => import(/* webpackChunkName: "p-Methodology" */ "@/pages/Methodology"));
const QualityAssurance  = lazyRoute("p-QualityAssurance", () => import(/* webpackChunkName: "p-QualityAssurance" */ "@/pages/QualityAssurance"));
const CaseStudy         = lazyRoute("p-CaseStudy", () => import(/* webpackChunkName: "p-CaseStudy" */ "@/pages/CaseStudy"));
const CaseStudiesHub    = lazyRoute("p-CaseStudiesHub", () => import(/* webpackChunkName: "p-CaseStudiesHub" */ "@/pages/CaseStudiesHub"));
const SitemapPage       = lazyRoute("p-SitemapPage", () => import(/* webpackChunkName: "p-SitemapPage" */ "@/pages/SitemapPage"));

// Admin pages — code-split: never loaded for public visitors, shaves ~150KB+ from the main bundle.
const AdminSEO = lazyRoute("p-AdminSEO", () => import(/* webpackChunkName: "p-AdminSEO" */ "@/pages/AdminSEO"));
const AdminArticles = lazyRoute("p-AdminArticles", () => import(/* webpackChunkName: "p-AdminArticles" */ "@/pages/AdminArticles"));
const AdminLeads = lazyRoute("p-AdminLeads", () => import(/* webpackChunkName: "p-AdminLeads" */ "@/pages/AdminLeads"));
const AdminEmailFailures = lazyRoute("p-AdminEmailFailures", () => import(/* webpackChunkName: "p-AdminEmailFailures" */ "@/pages/AdminEmailFailures"));
const AdminFCAReview = lazyRoute("p-AdminFCAReview", () => import(/* webpackChunkName: "p-AdminFCAReview" */ "@/pages/AdminFCAReview"));
const AdminAssessments = lazyRoute("p-AdminAssessments", () => import(/* webpackChunkName: "p-AdminAssessments" */ "@/pages/AdminAssessments"));
const AdminCost = lazyRoute("p-AdminCost", () => import(/* webpackChunkName: "p-AdminCost" */ "@/pages/AdminCost"));

const RouteFallback = () => (
  // min-h-screen prevents CLS when the lazy chunk swaps in — the fallback reserves the
  // same vertical space as a typical page hero, so the replacement doesn't shift content.
  <main className="min-h-screen bg-white" aria-busy="true" aria-live="polite">
    <div className="flex items-center justify-center pt-32">
      <div className="flex items-center gap-3 text-stone-400 text-[13px]">
        <div className="w-4 h-4 rounded-full border-2 border-stone-200 border-t-teal-700 animate-spin motion-reduce:hidden" />
        Loading…
      </div>
    </div>
  </main>
);
const AdminFallback = () => (
  <main className="min-h-screen grid place-items-center bg-stone-50">
    <div className="text-stone-500 text-[13px]">Loading admin…</div>
  </main>
);
const withSuspense = (El) => <Suspense fallback={<AdminFallback />}><El /></Suspense>;

function ServiceDispatcher() {
  const { slug } = useParams();
  if (LANDING_PAGE_SLUGS.includes(slug)) return <LandingPage />;
  return <ServiceDetail />;
}

// /blog/:slug → /resources/:slug (SPA replace, preserves SEO via canonical)
function BlogToResourceRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/resources/${slug}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function AppShell() {
  const { pathname } = useLocation();
  const isAds = pathname.startsWith("/lp/");
  const isAdmin = pathname.startsWith("/admin/");
  if (isAds) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AdsLayout>
          <Routes>
            <Route path="/lp/:slug" element={<AdsLandingPage />} />
          </Routes>
        </AdsLayout>
      </Suspense>
    );
  }
  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin/email-failures" element={withSuspense(AdminEmailFailures)} />
        <Route path="/admin/seo" element={withSuspense(AdminSEO)} />
        <Route path="/admin/articles" element={withSuspense(AdminArticles)} />
        <Route path="/admin/leads" element={withSuspense(AdminLeads)} />
        <Route path="/admin/fca-review" element={withSuspense(AdminFCAReview)} />
        <Route path="/admin/assessments" element={withSuspense(AdminAssessments)} />
        <Route path="/admin/cost" element={withSuspense(AdminCost)} />
      </Routes>
    );
  }
  return (
    <>
      <Header />
      <div className="flex-1">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/functional-capacity-assessment" element={<FCAMoneyPage />} />
          <Route path="/services/functional-capacity-assessments" element={<Navigate to="/services/functional-capacity-assessment" replace />} />
          <Route path="/services/:slug" element={<ServiceDispatcher />} />
          <Route path="/locations" element={<LocationsHub />} />
          <Route path="/locations/:slug" element={<LocationPage />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/ndis-fca-evidence-checklist" element={<ChecklistLandingPage />} />
          <Route path="/resources/sample-fca-report" element={<SampleFCAReport />} />
          <Route path="/resources/:slug" element={<Article />} />
          {/* /blog alias — preserves SEO via React-Router replace + canonical inside child page */}
          <Route path="/blog" element={<Navigate to="/resources" replace />} />
          <Route path="/blog/:slug" element={<BlogToResourceRedirect />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/book-an-appointment" element={<Contact asBooking />} />
          <Route path="/work-with-us" element={<WorkWithUs />} />
          <Route path="/refer-a-participant" element={<ReferParticipant />} />
          <Route path="/for-support-coordinators" element={<ForSupportCoordinators />} />
          <Route path="/support-coordinators" element={<SupportCoordinatorsPack />} />
          <Route path="/occupational-therapy-functional-capacity-assessment" element={<OTFunctionalCapacityAssessment />} />
          <Route path="/dsp-functional-capacity-assessment" element={<DSPFunctionalCapacityAssessment />} />
          <Route path="/resources/functional-capacity-assessment-dsp-application" element={<FCADSPApplicationArticle />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/pricing-and-funding" element={<Navigate to="/pricing" replace />} />
          <Route path="/cost" element={<Navigate to="/pricing" replace />} />
          <Route path="/careers" element={<Navigate to="/work-with-us" replace />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/thank-you" element={<ThankYou />} />
          {/* Topical Authority Hubs */}
          <Route path="/learn" element={<LearnHub />} />
          <Route path="/learn/:slug" element={<LearnArticle />} />
          {/* Case Studies hub + individual pages — override the generic HubPage routing */}
          <Route path="/case-studies" element={<CaseStudiesHub />} />
          <Route path="/case-studies/:slug" element={<CaseStudy />} />
          {/* Authority / trust pages */}
          <Route path="/why-trust-us" element={<WhyTrustUs />} />
          <Route path="/methodology" element={<Methodology />} />
          <Route path="/quality-assurance" element={<QualityAssurance />} />
          {/* HTML sitemap */}
          <Route path="/sitemap" element={<SitemapPage />} />
          {HUB_SLUGS
            .filter((slug) => slug !== "case-studies")
            .map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<HubPageRouted slug={slug} />} />
            ))}
          <Route path="/conditions/:conditionSlug" element={<ConditionPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </div>
      <Suspense fallback={null}>
        <PreFooterCTA />
      </Suspense>
      <Footer />
      <Suspense fallback={null}>
        <MobileStickyBookBar />
        <MobileStickyQuoteBar />
        <FloatingContact />
      </Suspense>
    </>
  );
}

function HubPageRouted({ slug }) {
  // Inject the hub slug as if it came from useParams (since each hub has a fixed path).
  return <HubPage hubSlugOverride={slug} />;
}

function App() {
  // Hide the persistent prerendered hero shell right before React's first paint
  // (it lives outside #__fca_react so it is never removed from the DOM — that
  // keeps its H1 as the stable LCP element). Runs before paint → no duplicate
  // hero and no layout shift.
  useLayoutEffect(() => {
    const shell = document.getElementById("__fca_critical_hero");
    if (shell) {
      // Hide, don't display:none — display:none destroys the layout objects and Chrome
      // then (racily) drops the shell's text as the recorded LCP candidate, re-attributing
      // LCP to this JS render. visibility:hidden keeps the paint record intact.
      Object.assign(shell.style, { visibility: "hidden", position: "absolute", height: "0", overflow: "hidden", pointerEvents: "none" });
      shell.setAttribute("aria-hidden", "true");
    }
  }, []);
  return (
    <div className="App min-h-screen flex flex-col bg-white">
      <BrowserRouter>
        <ScrollToTop />
        <SEOProvider>
          <PixelScripts />
          <AppShell />
        </SEOProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
